/**
 * LohnverrechnungPage — Lohnverrechnung-Archiv mit Unter-Routen:
 *   /lohnverrechnung          → Archiv (Monats-Übersicht)
 *   /lohnverrechnung/import   → Import-Seite (Upload + Preview + Erfolg)
 *   /lohnverrechnung/einstellungen → SMS-Config + Steuerberater-Info
 *
 * Design: Exakt nach RecruitingPage.jsx Vorlage (SKILL-design.md)
 *
 * @page /lohnverrechnung, /lohnverrechnung/import, /lohnverrechnung/einstellungen
 * @roles administrator, hr_manager
 * @since v2.266.0
 * @updated v2.270.1 — Recruiting-Layout, Import-Erfolgsmeldung, KPI brutto
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Upload, Download, X, FileText, Send, AlertTriangle, Check, Trash2,
  Users, Banknote, CalendarDays, HelpCircle, Settings, MessageSquare, Info,
  ChevronLeft, Eye, MoreHorizontal, Clock, UserCheck, UserX, RotateCcw, History,
  Search, CheckCircle, XCircle, Zap
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SharedLohnverrechnungTabs from '../components/SharedLohnverrechnungTabs';

const API = import.meta.env.VITE_API_URL || '';
const api = (path, opts) => fetch(`${API}${path}`, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const fmtEur = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('de-AT', { style: 'currency', currency: 'EUR', minimumFractionDigits: n >= 10 ? 0 : 2, maximumFractionDigits: n >= 10 ? 0 : 2 });
};
const fmtMonat = (m) => {
  if (!m) return '—';
  const [j, mm] = m.split('-');
  const namen = ['', 'Jänner','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
  return `${namen[parseInt(mm)] || mm} ${j}`;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Vienna' }) : '—';


// ═══════════════════════════════════════════════════════════════
//  Import Page (standalone, card-based layout)
// ═══════════════════════════════════════════════════════════════
function ImportView({ t }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [monat, setMonat] = useState(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [split, setSplit] = useState(null);
  const [splitDirty, setSplitDirty] = useState(false);
  const [datei, setDatei] = useState(null);
  const [existingImport, setExistingImport] = useState(null);
  const [smsSenden, setSmsSenden] = useState(false);
  const [entries, setEntries] = useState([]);
  const [maList, setMaList] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api('/api/lohnverrechnung/mitarbeiter-liste').then(setMaList).catch(() => {});
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/lohnverrechnung/import`, {
        method: 'POST', credentials: 'include', body: formData,
      }).then(r => r.json());

      if (!res.ok) { setError(res.error || 'Import fehlgeschlagen'); setLoading(false); return; }

      setDatei(res.datei);
      setPreview(res.preview);
      setStats(res.stats);
      setEntries(res.preview);
      setSplit(res.split || null);
      setSplitDirty(false);
      setExistingImport(res.existingImport);
      if (res.monat) setMonat(res.monat);
      setStep(2);
    } catch (err) {
      setError(err.message || 'Upload fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = (idx, maId) => {
    const updated = [...entries];
    const ma = maList.find(m => m.id === parseInt(maId));
    updated[idx] = {
      ...updated[idx],
      mitarbeiter_id: parseInt(maId) || null,
      mitarbeiter_name: ma?.name || null,
      match_method: maId ? 'manuell' : null,
      confidence: maId ? 100 : 0,
      ignoriert: false,
    };
    setEntries(updated);
    setSplitDirty(true);
  };

  const handleIgnore = (idx) => {
    const updated = [...entries];
    updated[idx] = {
      ...updated[idx],
      ignoriert: true,
      mitarbeiter_id: null,
      mitarbeiter_name: null,
      match_method: 'ignoriert',
      confidence: 0,
    };
    setEntries(updated);
    setSplitDirty(true);
  };

  const handleUnignore = (idx) => {
    const updated = [...entries];
    updated[idx] = {
      ...updated[idx],
      ignoriert: false,
      match_method: null,
      confidence: 0,
    };
    setEntries(updated);
    setSplitDirty(true);
  };

  const handleConfirm = async () => {
    setConfirming(true); setError(null);
    try {
      const res = await apiPost('/api/lohnverrechnung/import/bestaetigen', {
        datei, monat, entries, sms_senden: smsSenden,
      });
      if (res.ok) {
        setImportResult({
          monat,
          gesamt: stats?.gesamt || entries.length,
          gematcht: entries.filter(e => e.mitarbeiter_id).length,
          brutto: entries.reduce((s, e) => s + (parseFloat(e.brutto) || 0), 0),
          sms: smsSenden,
        });
        setStep(3);
      } else {
        setError(res.error || 'Bestätigung fehlgeschlagen');
        if (res.split) { setSplit(res.split); setSplitDirty(false); }
      }
    } catch (err) {
      setError(err.message || 'Fehler');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/lohnverrechnung"
          className="p-2 rounded-lg border border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
          <ChevronLeft size={16} className="text-[var(--text-secondary)]" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">
            {step === 1 ? t('lohnverrechnung.import.title') : step === 2 ? t('lohnverrechnung.import.preview') : t('lohnverrechnung.import.successTitle')}
          </h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('lohnverrechnung.import.subtitle')}</p>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-6 space-y-5">
          {/* Monat Picker */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('lohnverrechnung.import.monat')}</label>
            <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
              className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] w-48 focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none" />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('lohnverrechnung.import.datei')}</label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files[0]; if (f?.name?.endsWith('.pdf')) setFile(f); }}
              className="border-2 border-dashed border-[var(--border-default,#E5E7EB)] rounded-xl p-8 text-center cursor-pointer hover:border-[#338edc] transition-colors"
            >
              <Upload size={32} className="mx-auto mb-2 text-[var(--text-muted,#9CA3AF)]" />
              {file ? (
                <p className="text-sm font-medium text-[var(--text-primary)]">{file.name}</p>
              ) : (
                <p className="text-sm text-[var(--text-muted,#9CA3AF)]">{t('lohnverrechnung.import.dropzone')}</p>
              )}
              <input ref={fileRef} type="file" accept=".pdf" className="hidden"
                onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
            </div>
          </div>

          <button onClick={handleUpload} disabled={!file || loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
            {loading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {t('lohnverrechnung.import.start')}
          </button>
        </div>
      )}

      {/* Step 2: Preview & Assign */}
      {step === 2 && preview && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: t('lohnverrechnung.import.gesamt'), value: stats?.gesamt || 0 },
              { label: t('lohnverrechnung.import.matched'), value: entries.filter(e => e.mitarbeiter_id).length },
              { label: t('lohnverrechnung.import.ignored'), value: entries.filter(e => e.ignoriert).length },
              { label: t('lohnverrechnung.import.unmatched'), value: entries.filter(e => !e.mitarbeiter_id && !e.ignoriert).length },
            ].map((kpi, i) => (
              <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-1">{kpi.label}</p>
                <p className="text-xl font-bold text-[var(--text-primary)]">{kpi.value}</p>
              </div>
            ))}
          </div>

          {existingImport && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2">
              <AlertTriangle size={14} />
              {t('lohnverrechnung.import.existingWarning', { datum: fmtDate(existingImport.importiert_am) })}
            </div>
          )}

          {/* #1319 — Seiten-Zuordnung (deterministischer Split) + Freigabe-Gate */}
          {split && (
            <div className={`border rounded-xl p-4 ${split.ok
              ? 'border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)]'
              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'}`}>
              <div className="flex items-center gap-2 mb-2">
                {split.ok
                  ? <CheckCircle size={16} className="text-green-600" />
                  : <XCircle size={16} className="text-red-600" />}
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('lohnverrechnung.import.splitTitle')}
                </p>
              </div>
              <p className={`text-sm mb-2 ${split.ok ? 'text-[var(--text-secondary)]' : 'text-red-700 dark:text-red-300'}`}>
                {split.ok ? t('lohnverrechnung.import.splitOk') : t('lohnverrechnung.import.splitFail')}
              </p>

              {splitDirty && (
                <p className="text-xs text-[var(--text-tertiary,#6B7280)] mb-2">{t('lohnverrechnung.import.splitDirty')}</p>
              )}

              {!split.ok && split.errors?.length > 0 && (
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 mb-3 list-disc pl-5">
                  {split.errors.map((er, i) => <li key={i}>{er.detail}</li>)}
                </ul>
              )}

              {split.assignments?.filter(a => a.mitarbeiter_id).length > 0 && (
                <div className="text-xs text-[var(--text-secondary)] space-y-0.5 max-h-48 overflow-auto">
                  {split.assignments.filter(a => a.mitarbeiter_id).map((a, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-0.5">
                      <span className="text-[var(--text-primary)]">{a.name}</span>
                      <span className="font-mono text-[var(--text-tertiary,#6B7280)]">
                        {t('lohnverrechnung.import.seiten')} {a.pages.join(', ')}
                      </span>
                    </div>
                  ))}
                  {split.collectivePages?.length > 0 && (
                    <div className="pt-1.5 mt-1.5 border-t border-[var(--border-default,#E5E7EB)] text-[var(--text-tertiary,#6B7280)]">
                      {t('lohnverrechnung.import.sammelSeiten')}: {split.collectivePages.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Preview table */}
          <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">DN-Nr</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.name')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">SVNR</th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.brutto')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.zuordnung')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {entries.map((e, i) => (
                  <tr key={i} className={`hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors ${e.ignoriert ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 text-[var(--text-secondary)] font-mono text-xs">{e.rzl_nr || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-primary)] font-medium">{e.name || '—'}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] font-mono text-xs">{e.svnr || '—'}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(e.brutto)}</td>
                    <td className="px-4 py-3">
                      {e.mitarbeiter_id ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                          {e.mitarbeiter_name}
                        </span>
                      ) : e.ignoriert ? (
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {t('lohnverrechnung.import.ignoriertBadge')}
                          </span>
                          <button type="button" onClick={() => handleUnignore(i)}
                            className="text-xs text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] underline-offset-2 hover:underline">
                            {t('lohnverrechnung.import.unignore')}
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            className="text-xs border border-[var(--border-default,#E5E7EB)] rounded-lg px-2 py-1.5 bg-[var(--bg-card,#fff)] text-[var(--text-primary)] max-w-[200px] focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none"
                            value=""
                            onChange={ev => handleAssign(i, ev.target.value)}
                          >
                            <option value="">{t('lohnverrechnung.import.selectMa')}</option>
                            {maList.filter(m => m.ist_aktiv).map(m => (
                              <option key={m.id} value={m.id}>{m.name}{m.svnr ? ` (${m.svnr.substring(0, 4)})` : ''}</option>
                            ))}
                          </select>
                          <button type="button" onClick={() => handleIgnore(i)}
                            title={t('lohnverrechnung.import.ignoreHint')}
                            className="text-xs px-2 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] hover:text-[var(--text-primary)] transition-colors">
                            {t('lohnverrechnung.import.ignore')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* SMS Checkbox */}
          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
            <input type="checkbox" checked={smsSenden} onChange={e => setSmsSenden(e.target.checked)}
              className="rounded border-[var(--border-default,#E5E7EB)]" />
            <Send size={14} /> {t('lohnverrechnung.import.smsCheckbox')}
          </label>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button onClick={() => setStep(1)}
              className="px-4 py-2.5 rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
              {t('common.back')}
            </button>
            <button onClick={handleConfirm} disabled={confirming || (split && !split.ok && !splitDirty)}
              title={split && !split.ok && !splitDirty ? t('lohnverrechnung.import.splitFail') : undefined}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {confirming && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {t('lohnverrechnung.import.confirm')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && importResult && (
        <div className="border border-green-200 dark:border-green-800 rounded-xl p-8 bg-green-50/50 dark:bg-green-900/10 text-center space-y-5">
          <div className="w-14 h-14 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
            <Check size={28} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[var(--text-primary)]">{t('lohnverrechnung.import.successTitle')}</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t('lohnverrechnung.import.successDesc', { monat: fmtMonat(importResult.monat) })}</p>
          </div>
          <div className="inline-grid grid-cols-3 gap-4 text-left">
            {[
              { label: t('lohnverrechnung.import.gesamt'), value: importResult.gesamt },
              { label: t('lohnverrechnung.import.matched'), value: importResult.gematcht },
              { label: t('lohnverrechnung.col.brutto'), value: fmtEur(importResult.brutto) },
            ].map((kpi, i) => (
              <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 bg-[var(--bg-card,#fff)]">
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{kpi.label}</p>
                <p className="text-xl font-bold text-[var(--text-primary)]">{kpi.value}</p>
              </div>
            ))}
          </div>
          {importResult.sms && (
            <p className="text-sm text-[var(--text-secondary)] flex items-center gap-1.5 justify-center">
              <Send size={14} className="text-green-600" /> {t('lohnverrechnung.import.smsQueued')}
            </p>
          )}
          <Link to="/lohnverrechnung"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors">
            {t('lohnverrechnung.import.toArchive')}
          </Link>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Einstellungen View — SMS Config + Steuerberater-Info
// ═══════════════════════════════════════════════════════════════
function EinstellungenView({ t }) {
  const [smsConfig, setSmsConfig] = useState({ mode: 'active', test_redirect: '' });
  const [saving, setSaving] = useState(false);
  const [ignored, setIgnored] = useState([]);

  const loadIgnored = useCallback(() => {
    api('/api/lohnverrechnung/ignorierte').then(d => setIgnored(d.rows || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api('/api/sms/send-config').then(d => { if (d.config) setSmsConfig(d.config); }).catch(() => {});
    loadIgnored();
  }, [loadIgnored]);

  const handleUnignore = async (svnr) => {
    try {
      await fetch(`${API}/api/lohnverrechnung/ignorierte/${encodeURIComponent(svnr)}`, {
        method: 'DELETE', credentials: 'include',
      });
      loadIgnored();
    } catch { /* */ }
  };

  const saveSms = async (updates) => {
    const next = { ...smsConfig, ...updates };
    setSmsConfig(next);
    setSaving(true);
    try {
      await apiPut('/api/sms/send-config', next);
    } catch { /* */ }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/lohnverrechnung"
          className="p-2 rounded-lg border border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
          <ChevronLeft size={16} className="text-[var(--text-secondary)]" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('lohnverrechnung.einstellungen.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('lohnverrechnung.einstellungen.subtitle')}</p>
        </div>
      </div>

      {/* SMS Config Card */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--border-default,#E5E7EB)]">
          <MessageSquare size={16} className="text-[var(--text-secondary)]" />
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('lohnverrechnung.einstellungen.smsTitle')}</h2>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('lohnverrechnung.einstellungen.smsMode')}</label>
            <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
              {[
                { key: 'active', label: t('sms.modeActive', 'Aktiv'), dot: 'bg-green-500' },
                { key: 'test', label: t('sms.modeTest', 'Test'), dot: 'bg-amber-500' },
                { key: 'disabled', label: t('sms.modeDisabled', 'Aus'), dot: 'bg-red-500' },
              ].map(m => (
                <button key={m.key} onClick={() => saveSms({ mode: m.key })}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-[8px] transition-all ${
                    smsConfig.mode === m.key
                      ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Test Redirect Number */}
          {smsConfig.mode === 'test' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('lohnverrechnung.einstellungen.redirectNr')}</label>
              <input
                type="text"
                value={smsConfig.test_redirect}
                onChange={e => setSmsConfig(prev => ({ ...prev, test_redirect: e.target.value }))}
                onBlur={() => saveSms({ test_redirect: smsConfig.test_redirect })}
                placeholder="+436701234567"
                className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] w-64 focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none"
              />
              <p className="text-xs text-[var(--text-muted,#9CA3AF)] mt-1">{t('lohnverrechnung.einstellungen.redirectHint')}</p>
            </div>
          )}

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
            <Info size={14} className="text-[var(--text-muted,#9CA3AF)] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[var(--text-secondary)]">{t('lohnverrechnung.einstellungen.smsInfo')}</p>
          </div>
        </div>
      </div>

      {/* Steuerberater Info Card */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--border-default,#E5E7EB)]">
          <Banknote size={16} className="text-[var(--text-secondary)]" />
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('lohnverrechnung.einstellungen.steuerberaterTitle')}</h2>
        </div>
        <div className="px-5 py-4">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
            <Info size={14} className="text-[var(--text-muted,#9CA3AF)] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[var(--text-secondary)]">{t('lohnverrechnung.einstellungen.steuerberaterInfo')}</p>
          </div>
        </div>
      </div>

      {/* Ignorier-Liste Card (v2.373.1 #866) */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-default,#E5E7EB)]">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[var(--text-secondary)]" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">{t('lohnverrechnung.einstellungen.ignoredTitle')}</h2>
          </div>
          <span className="text-xs text-[var(--text-tertiary,#6B7280)]">{ignored.length}</span>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs text-[var(--text-secondary)] mb-3">{t('lohnverrechnung.einstellungen.ignoredInfo')}</p>
          {ignored.length === 0 ? (
            <p className="text-sm text-[var(--text-muted,#9CA3AF)] py-4 text-center">{t('lohnverrechnung.einstellungen.ignoredEmpty')}</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-[var(--border-default,#E5E7EB)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">SVNR</th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.name')}</th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">DN-Nr</th>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.einstellungen.ignoredSince')}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                  {ignored.map((row) => (
                    <tr key={row.svnr} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
                      <td className="px-3 py-2 text-[var(--text-secondary)] font-mono text-xs">{row.svnr}</td>
                      <td className="px-3 py-2 text-[var(--text-primary)]">{row.name || '—'}</td>
                      <td className="px-3 py-2 text-[var(--text-secondary)] font-mono text-xs">{row.rzl_nr || '—'}</td>
                      <td className="px-3 py-2 text-[var(--text-tertiary,#6B7280)] text-xs">{fmtDate(row.ignoriert_am)}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => handleUnignore(row.svnr)}
                          className="text-xs px-2 py-1 rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] hover:text-[var(--text-primary)] transition-colors">
                          {t('lohnverrechnung.einstellungen.removeIgnored')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Detail View — Lohnzettel eines Monats
// ═══════════════════════════════════════════════════════════════
function DetailView({ monat, onClose, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maList, setMaList] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [sendingSms, setSendingSms] = useState(null); // lohnzettel ID currently sending
  const [smsMsg, setSmsMsg] = useState(null); // { type: 'success'|'error', text: '...' }

  const load = useCallback(() => {
    setLoading(true);
    api(`/api/lohnverrechnung/detail/${monat}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [monat]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/api/lohnverrechnung/mitarbeiter-liste').then(setMaList).catch(() => {});
  }, []);

  const handleAssign = async (lzId, maId) => {
    if (!maId) return;
    setAssigning(lzId);
    try {
      await fetch(`${API}/api/lohnverrechnung/${lzId}/zuordnung`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: parseInt(maId) }),
      }).then(r => r.json());
      load();
    } catch { /* */ }
    finally { setAssigning(null); }
  };

  const handleSmsEinzel = async (lzId) => {
    setSendingSms(lzId);
    setSmsMsg(null);
    try {
      const res = await apiPost(`/api/lohnverrechnung/sms-einzel/${lzId}`, {});
      if (res.ok || res.test_mode) {
        const hint = res.test_mode ? ` (Test → ${res.telefon})` : ` → ${res.telefon}`;
        setSmsMsg({ type: 'success', text: `SMS ${t('lohnverrechnung.smsSent')}${hint}` });
      } else {
        setSmsMsg({ type: 'error', text: res.error || 'SMS-Versand fehlgeschlagen' });
      }
      load();
    } catch (err) {
      setSmsMsg({ type: 'error', text: err?.error || err?.message || 'SMS-Versand fehlgeschlagen' });
    } finally { setSendingSms(null); }
  };

  const handleSmsBulk = async () => {
    const count = data?.zettel?.filter(z => z.mitarbeiter_id && z.ma_telefon && !z.sms_gesendet).length || 0;
    if (!count) return;
    if (!confirm(t('lohnverrechnung.smsBulkConfirm', { count }))) return;
    setSmsMsg(null);
    try {
      const res = await apiPost(`/api/lohnverrechnung/${monat}/sms`, {});
      if (res.sent > 0 || res.empfaenger > 0) {
        setSmsMsg({ type: 'success', text: `${res.sent}/${res.empfaenger} SMS versendet${res.failed ? `, ${res.failed} fehlgeschlagen` : ''}` });
      } else {
        setSmsMsg({ type: 'error', text: res.error || 'Keine SMS versendet' });
      }
      load();
    } catch (err) {
      setSmsMsg({ type: 'error', text: err?.error || err?.message || 'SMS-Versand fehlgeschlagen' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose}
            className="p-2 rounded-lg border border-[var(--border-default,#E5E7EB)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
            <ChevronLeft size={16} className="text-[var(--text-secondary)]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">{fmtMonat(monat)}</h1>
            <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">
              {data?.zettel?.length || 0} {t('lohnverrechnung.col.name', 'Mitarbeiter')}
              {data?.import?.sms_versendet && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">SMS</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.import?.journal_dateipfad && (
            <a href={`${API}/api/lohnverrechnung/download-journal/${monat}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[10px] border border-[var(--border-default,#E5E7EB)] text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
              <FileText size={14} /> Journal
            </a>
          )}
          <button onClick={handleSmsBulk}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold transition-colors">
            <Send size={14} /> {t('lohnverrechnung.smsBulkBtn')}
          </button>
        </div>
      </div>

      {/* SMS Feedback Toast */}
      {smsMsg && (
        <div className={`p-3 rounded-xl border text-sm flex items-center justify-between gap-2 ${
          smsMsg.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
        }`}>
          <span className="flex items-center gap-2">
            {smsMsg.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
            {smsMsg.text}
          </span>
          <button onClick={() => setSmsMsg(null)} className="opacity-60 hover:opacity-100"><X size={14} /></button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data?.zettel?.length ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.name')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">SVNR</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.brutto')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.netto')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.dgKosten')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.match')}</th>
                <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">SMS</th>
                <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.download')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {data.zettel.map((z) => (
                <tr key={z.id} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
                  <td className="px-4 py-3">
                    {z.mitarbeiter_id ? (
                      <span className="text-[var(--text-primary)] font-medium">{z.ma_name}</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-[var(--text-muted,#9CA3AF)] italic">{z.dateiname?.split('_')[0] || '?'}</span>
                        <select
                          className="text-xs border border-amber-300 dark:border-amber-700 rounded-lg px-2 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-[var(--text-primary)] max-w-[160px]"
                          value="" disabled={assigning === z.id}
                          onChange={e => handleAssign(z.id, e.target.value)}
                        >
                          <option value="">{t('lohnverrechnung.import.selectMa')}</option>
                          {maList.filter(m => m.ist_aktiv).map(m => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)] font-mono text-xs">{z.svnr || z.ma_svnr || '—'}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(z.brutto)}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(z.netto)}</td>
                  <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(z.dg_gesamtkosten)}</td>
                  <td className="px-4 py-3">
                    {z.match_methode && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        z.match_konfidenz >= 90
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : z.match_konfidenz >= 70
                          ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {z.match_methode} ({z.match_konfidenz}%)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {z.mitarbeiter_id && (
                      z.sms_gesendet ? (
                        <span className="inline-flex items-center justify-center w-8 h-8 text-green-600 dark:text-green-400" title={t('lohnverrechnung.smsSent')}>
                          <Check size={14} />
                        </span>
                      ) : z.ma_telefon ? (
                        <button onClick={() => handleSmsEinzel(z.id)} disabled={sendingSms === z.id}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-[var(--text-muted,#9CA3AF)] hover:text-[#338edc] disabled:opacity-50"
                          title={t('lohnverrechnung.smsEinzelTitle')}>
                          {sendingSms === z.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Send size={14} />
                          )}
                        </button>
                      ) : (
                        <span className="text-[var(--text-muted,#9CA3AF)]" title={t('lohnverrechnung.smsNoPhone')}>—</span>
                      )
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {z.mitarbeiter_id && (
                      <a href={`${API}/api/lohnverrechnung/download/${z.id}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors text-[var(--text-muted,#9CA3AF)] hover:text-[#338edc]">
                        <Download size={14} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Summen */}
            <tfoot>
              <tr className="border-t-2 border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] font-semibold">
                <td className="px-4 py-3 text-[var(--text-primary)]">{t('lohnverrechnung.summe')}</td>
                <td />
                <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(data.summen.brutto)}</td>
                <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(data.summen.netto)}</td>
                <td className="px-4 py-3 text-right text-[var(--text-primary)]">{fmtEur(data.summen.dg_gesamtkosten)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-12 bg-[var(--bg-card,#fff)] text-center">
          <FileText size={48} className="mx-auto text-[var(--text-tertiary,#6B7280)] mb-4" />
          <p className="text-base font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.noData')}</p>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Archive View — Monats-Übersicht (Hauptseite, Recruiting-Layout)
// ═══════════════════════════════════════════════════════════════
function ArchiveView({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonat, setSelectedMonat] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api('/api/lohnverrechnung/uebersicht')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (monat, e) => {
    e.stopPropagation();
    if (!confirm(t('lohnverrechnung.deleteConfirm', { monat: fmtMonat(monat) }))) return;
    await fetch(`${API}/api/lohnverrechnung/import/${monat}`, {
      method: 'DELETE', credentials: 'include',
    }).then(r => r.json());
    load();
  };

  if (selectedMonat) {
    return <DetailView monat={selectedMonat} onClose={() => { setSelectedMonat(null); load(); }} t={t} />;
  }

  const kpis = data?.kpis;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('lohnverrechnung.title')}</h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('lohnverrechnung.subtitle')}</p>
        </div>
        <Link to="/lohnverrechnung/import"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors">
          <Upload size={16} /> {t('lohnverrechnung.importBtn')}
        </Link>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('lohnverrechnung.kpi.letzterMonat'), value: fmtMonat(kpis.letzter_monat), icon: CalendarDays, color: 'text-[#338edc]' },
            { label: t('lohnverrechnung.kpi.anzahlMa'), value: kpis.letzte_anzahl, icon: Users, color: 'text-[#6366F1]' },
            { label: t('lohnverrechnung.kpi.bruttoGesamt'), value: fmtEur(kpis.letzte_brutto), icon: Banknote, color: 'text-[#10B981]' },
            { label: t('lohnverrechnung.kpi.dgKosten'), value: fmtEur(kpis.letzte_dg_kosten), icon: Banknote, color: 'text-[#F59E0B]' },
          ].map((kpi, i) => (
            <div key={i} className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 bg-[var(--bg-card,#fff)]">
              <div className="flex items-center gap-2 mb-1">
                <kpi.icon size={14} className={kpi.color} />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{kpi.label}</p>
              </div>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Monats-Tabelle */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data?.importe?.length ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.monat')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.maAnzahl')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.brutto')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.dgKosten')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">Status</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.importiert')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.aktionen')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {data.importe.map(imp => {
                const offen = parseInt(imp.offene_zuordnungen || 0);
                return (
                  <tr key={imp.id} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors cursor-pointer"
                    onClick={() => setSelectedMonat(imp.monat)}>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">{fmtMonat(imp.monat)}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{imp.anzahl_zugeordnet}/{imp.anzahl_gesamt}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{fmtEur(imp.brutto_gesamt)}</td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{fmtEur(imp.dg_kosten_gesamt)}</td>
                    <td className="px-4 py-3">
                      {offen === 0 ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                          {t('lohnverrechnung.status.complete')}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          {offen} {t('lohnverrechnung.status.open')}
                        </span>
                      )}
                      {imp.sms_versendet && (
                        <span className="ml-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          SMS
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-tertiary,#6B7280)]">{fmtDate(imp.importiert_am)}</td>
                    <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setSelectedMonat(imp.monat)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                          <Eye size={12} /> Detail
                        </button>
                        <button onClick={(e) => handleDelete(imp.monat, e)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-[var(--text-muted,#9CA3AF)] hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-12 bg-[var(--bg-card,#fff)] text-center">
          <FileText size={48} className="mx-auto text-[var(--text-tertiary,#6B7280)] mb-4" />
          <p className="text-base font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.empty.title')}</p>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('lohnverrechnung.empty.hint')}</p>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Main Page — Route-basierter View-Switch
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
//  LV-Report View — Stundenbericht fuer Steuerberater (#891)
// ═══════════════════════════════════════════════════════════════
function LvReportView({ t }) {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [jahr, setJahr] = useState(prev.getFullYear());
  const [monat, setMonat] = useState(prev.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true); setErr(null); setSummary(null);
    api(`/api/lv-report/${jahr}/${monat}/data`)
      .then(d => setSummary(d))
      .catch(e => setErr(e?.error || 'Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [jahr, monat]);

  const download = (format) => {
    const url = `${API}/api/lv-report/${jahr}/${monat}?format=${format}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `lv-report-${jahr}-${String(monat).padStart(2, '0')}.${format}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const monatNamen = ['', t('months.january', 'Jänner'), t('months.february', 'Februar'),
    t('months.march', 'März'), t('months.april', 'April'), t('months.may', 'Mai'),
    t('months.june', 'Juni'), t('months.july', 'Juli'), t('months.august', 'August'),
    t('months.september', 'September'), t('months.october', 'Oktober'),
    t('months.november', 'November'), t('months.december', 'Dezember')];

  const jahre = [];
  for (let y = now.getFullYear(); y >= 2020; y--) jahre.push(y);

  const minToH = (m) => ((m || 0) / 60).toFixed(2);
  const sumKey = (key) => summary?.rows?.reduce((a, r) => a + (r[key] || 0), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('lvReport.title', 'Stundenbericht — Lohnverrechnung')}</h1>
        <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">
          {t('lvReport.subtitle', 'Dedizierter Monatsbericht für Steuerberater — nur Stunden, keine Euro.')}
        </p>
      </div>

      {/* Monat-Picker + Download */}
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)] p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('lvReport.year', 'Jahr')}</label>
            <select value={jahr} onChange={e => setJahr(parseInt(e.target.value))}
              className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-white dark:bg-[var(--bg-card)]">
              {jahre.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('lvReport.month', 'Monat')}</label>
            <select value={monat} onChange={e => setMonat(parseInt(e.target.value))}
              className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-white dark:bg-[var(--bg-card)]">
              {monatNamen.slice(1).map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
            </select>
          </div>
          <div className="flex-1" />
          <button onClick={() => download('xlsx')} disabled={loading || !summary?.rows?.length}
            className="px-4 py-2 bg-[#338edc] text-white rounded-lg text-sm font-medium hover:bg-[#2a7bc0] disabled:opacity-50 flex items-center gap-2">
            <Download size={16} /> Excel
          </button>
          <button onClick={() => download('pdf')} disabled={loading || !summary?.rows?.length}
            className="px-4 py-2 bg-white border border-[var(--border-default,#E5E7EB)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-card-hover,#FAFAFE)] disabled:opacity-50 flex items-center gap-2">
            <FileText size={16} /> PDF
          </button>
        </div>
      </div>

      {/* Summary */}
      {loading && <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Lade…')}</div>}
      {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg p-3">{err}</div>}
      {summary && !loading && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card,#fff)]">
          <div className="px-5 py-4 border-b border-[var(--border-default,#E5E7EB)] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">
              {t('lvReport.summary', 'Vorschau')} — {monatNamen[monat]} {jahr}
            </h2>
            <span className="text-xs text-[var(--text-tertiary)]">
              {summary.ma_count} {t('lvReport.employees', 'Mitarbeiter')}
            </span>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['lvReport.sollHours', 'SOLL', minToH(sumKey('soll_min'))],
              ['lvReport.istHours', 'IST', minToH(sumKey('ist_min'))],
              ['lvReport.deltaHours', 'Delta', minToH(sumKey('delta_min'))],
              ['lvReport.mehrHours', 'Mehrstunden', minToH(sumKey('mehr_min'))],
              ['lvReport.ueberHours', 'Überstunden', minToH(sumKey('ueber_min'))],
              ['lvReport.krankHours', 'Krank', minToH(sumKey('krank_min'))],
              ['lvReport.urlaubHours', 'Urlaub', minToH(sumKey('urlaub_min'))],
              ['lvReport.ftBezahltHours', 'FT bezahlt', minToH(sumKey('ft_bezahlt_min'))],
              ['lvReport.ftGearbHours', 'FT gearbeitet', minToH(sumKey('ft_gearb_min'))],
              ['lvReport.sonntagHours', 'Sonntag', minToH(sumKey('sonntag_min'))],
              ['lvReport.nachtHours', 'Nachtarbeit', minToH(sumKey('nacht_min'))],
            ].map(([key, fallback, val]) => (
              <div key={key} className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3">
                <div className="text-xs text-[var(--text-tertiary,#6B7280)]">{t(key, fallback)}</div>
                <div className="text-lg font-semibold text-[var(--text-primary)] mt-1">{val} h</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-[var(--border-default,#E5E7EB)] text-xs text-[var(--text-tertiary)]">
            {t('lvReport.nightHint', 'Nachtarbeit-Fenster')}: {Math.floor(summary.nacht_von / 60).toString().padStart(2, '0')}:{(summary.nacht_von % 60).toString().padStart(2, '0')}–{Math.floor(summary.nacht_bis / 60).toString().padStart(2, '0')}:{(summary.nacht_bis % 60).toString().padStart(2, '0')}  •  {t('lvReport.poolHint', 'Pool-Accounts ausgeschlossen')}  •  {t('lvReport.noEuroHint', 'Nur Stunden — keine Euro-Werte')}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
//  Monats-Bestaetigung View (#892) — HR-Freigabe + DN-Status
// ═══════════════════════════════════════════════════════════════
// Earney-Style: Pill-Farben (bg=hellsfarbe, color=Vollfarbe, border=Vollfarbe für aktiven Zustand)
const BEST_STATUS = {
  offen:             { label: 'Offen',             cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',          icon: Clock,         iconColor: 'text-[#6B7280]', pillBg: 'var(--bg-inset,#F8F9FC)', pillColor: 'var(--text-secondary,#4B5563)' },
  hr_freigegeben:    { label: 'HR freigegeben',    cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',         icon: Send,          iconColor: 'text-[#338edc]', pillBg: '#EBF4FD',                 pillColor: '#338edc' },
  dn_bestaetigt:     { label: 'Bestaetigt',        cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',     icon: CheckCircle,   iconColor: 'text-[#10B981]', pillBg: '#ECFDF5',                 pillColor: '#10B981' },
  dn_abgelehnt:      { label: 'Abgelehnt',         cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',             icon: XCircle,       iconColor: 'text-[#EF4444]', pillBg: '#FEF2F2',                 pillColor: '#EF4444' },
  frist_abgelaufen:  { label: 'Frist abgelaufen',  cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',     icon: AlertTriangle, iconColor: 'text-[#F59E0B]', pillBg: '#FFFBEB',                 pillColor: '#F59E0B' },
};

function MonatsBestaetigungView({ t }) {
  const [monat, setMonat] = useState(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetGrund, setResetGrund] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('offen');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const monatApi = monat + '-01';

  const load = useCallback(() => {
    setLoading(true);
    api(`/api/zeitkonto?monat=${encodeURIComponent(monatApi)}`)
      .then(r => setRows(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [monatApi]);

  useEffect(() => { load(); }, [load]);

  // Reset selection on filter or month change
  useEffect(() => { setSelectedIds(new Set()); }, [activeFilter, monat]);

  const showMsg = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  const [jahr, mm] = monat.split('-');

  const doAction = async (ma, action, payload = {}) => {
    setBusyId(ma.mitarbeiter_id);
    setOpenMenu(null);
    try {
      const path = `/api/zeitkonto/${ma.mitarbeiter_id}/${jahr}-${parseInt(mm, 10)}/${action}`;
      const res = await apiPost(path, payload);
      if (res.success) {
        if (action === 'hr-freigabe') {
          showMsg('success', res.sms?.success
            ? t('zeitkontoBestaetigung.admin.freigabeSmsOk', 'Freigegeben + SMS gesendet')
            : t('zeitkontoBestaetigung.admin.freigabeNoSms', 'Freigegeben, aber SMS fehlgeschlagen: ') + (res.sms?.error || ''));
        } else if (action === 'resend-sms') {
          showMsg(res.sms?.success ? 'success' : 'error',
            res.sms?.success ? t('zeitkontoBestaetigung.admin.resendOk', 'SMS erneut gesendet') : (res.sms?.error || 'Fehler'));
        } else if (action === 'zuruecksetzen') {
          showMsg('success', t('zeitkontoBestaetigung.admin.resetOk', 'Zurueckgesetzt'));
          setResetFor(null); setResetGrund('');
        }
        load();
      } else {
        showMsg('error', res.error || 'Fehler');
      }
    } catch (err) {
      showMsg('error', err?.error || err?.message || 'Fehler');
    } finally {
      setBusyId(null);
    }
  };

  const doReset = () => {
    if (!resetFor) return;
    if (resetGrund.trim().length < 3) {
      showMsg('error', t('zeitkontoBestaetigung.admin.resetGrundZuKurz', 'Grund mindestens 3 Zeichen'));
      return;
    }
    doAction(resetFor, 'zuruecksetzen', { grund: resetGrund.trim() });
  };

  const counts = rows.reduce((acc, r) => {
    const s = r.bestaetigungs_status || 'offen';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const filteredRows = rows.filter(r => {
    const status = r.bestaetigungs_status || 'offen';
    if (activeFilter !== 'alle' && status !== activeFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const name = `${r.vorname || ''} ${r.nachname || ''}`.toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  // #1699 — nur MAs anbieten, die der Backend-Guard auch akzeptiert
  // (Pool/ohne Nummer wurden zuvor selektierbar angeboten → 400 → "0 freigegeben, 1 Fehler")
  // #1709 — Gleitzeit-Block entfernt (Backend-Guard #893 entfernt, Gleitzeit-MA versendbar)
  const freigabeBlockGrund = (r) => {
    if (r.ist_pool_account) return t('zeitkontoBestaetigung.admin.blockPool', 'Pool-Account — ausgenommen');
    if (!r.has_mobile_phone) return t('zeitkontoBestaetigung.admin.blockKeineNummer', 'Keine Telefonnummer hinterlegt');
    return null;
  };

  const isFreigabeFaehig = (r) => {
    const status = r.bestaetigungs_status || 'offen';
    return (status === 'offen' || status === 'frist_abgelaufen') && r.mitarbeiter_id && !freigabeBlockGrund(r);
  };

  const visibleFreigabeFaehig = filteredRows.filter(isFreigabeFaehig);
  const allVisibleSelected = visibleFreigabeFaehig.length > 0 && visibleFreigabeFaehig.every(r => selectedIds.has(r.mitarbeiter_id));
  const someVisibleSelected = visibleFreigabeFaehig.some(r => selectedIds.has(r.mitarbeiter_id));

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allVisibleSelected) {
      visibleFreigabeFaehig.forEach(r => next.delete(r.mitarbeiter_id));
    } else {
      visibleFreigabeFaehig.forEach(r => next.add(r.mitarbeiter_id));
    }
    setSelectedIds(next);
  };

  const toggleSelectRow = (maId) => {
    const next = new Set(selectedIds);
    if (next.has(maId)) next.delete(maId); else next.add(maId);
    setSelectedIds(next);
  };

  const runBulkFreigabe = async (mas) => {
    if (mas.length === 0) {
      showMsg('error', t('zeitkontoBestaetigung.admin.bulkKeineMa', 'Keine MAs zum Freigeben'));
      return;
    }
    setBulkRunning(true);
    let okCount = 0, smsOk = 0;
    const fehler = []; // #1699 — Fehlerursache pro MA sammeln statt verschlucken
    for (const ma of mas) {
      const name = `${ma.vorname || ''} ${ma.nachname || ''}`.trim();
      try {
        const path = `/api/zeitkonto/${ma.mitarbeiter_id}/${jahr}-${parseInt(mm, 10)}/hr-freigabe`;
        const res = await apiPost(path, {});
        if (res?.success) {
          okCount++;
          if (res?.sms?.success) smsOk++;
          else fehler.push(`${name}: SMS — ${res?.sms?.error || 'Fehler'}`);
        } else {
          fehler.push(`${name}: ${res?.error || 'Fehler'}`);
        }
      } catch (err) {
        fehler.push(`${name}: ${err?.error || err?.message || 'Fehler'}`);
      }
    }
    setBulkRunning(false);
    setSelectedIds(new Set());
    const errCount = fehler.length;
    let msg = t('zeitkontoBestaetigung.admin.bulkResultat',
      '{{ok}} MAs freigegeben, {{sms}} SMS versendet{{err}}',
      { ok: okCount, sms: smsOk, err: errCount > 0 ? `, ${errCount} ${t('zeitkontoBestaetigung.admin.bulkFehler', 'Fehler')}` : '' });
    if (errCount > 0) {
      msg += ` — ${fehler.slice(0, 3).join(' · ')}${errCount > 3 ? ' · …' : ''}`;
    }
    showMsg(errCount > 0 ? 'error' : 'success', msg);
    load();
  };

  const doBulkFreigabeSelected = () => {
    const mas = rows.filter(r => selectedIds.has(r.mitarbeiter_id) && isFreigabeFaehig(r));
    runBulkFreigabe(mas);
  };

  const doQuickFreigabeAlleOffenen = () => {
    const mas = rows.filter(r => (r.bestaetigungs_status || 'offen') === 'offen' && isFreigabeFaehig(r));
    runBulkFreigabe(mas);
  };

  // #1699 — Button zählt nur wirklich freigabefähige (Gleitzeit/Pool/ohne Nummer würden 400 werfen)
  const offenCount = rows.filter(r => (r.bestaetigungs_status || 'offen') === 'offen' && isFreigabeFaehig(r)).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{t('zeitkontoBestaetigung.admin.title', 'Monats-Bestaetigung')}</h1>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] mt-1">{t('zeitkontoBestaetigung.admin.subtitle', 'Freigabe + Status der DN-Bestaetigung via SMS')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)] pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('zeitkontoBestaetigung.admin.suchePlaceholder', 'Mitarbeiter suchen …')}
              className="pl-9 pr-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] w-56 focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none"
            />
          </div>
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)}
            className="px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] w-44 focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none" />
          <button
            type="button"
            disabled={offenCount === 0 || bulkRunning}
            onClick={doQuickFreigabeAlleOffenen}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/40 text-white text-sm font-semibold whitespace-nowrap"
            title={t('zeitkontoBestaetigung.admin.quickAllOffenHint', 'Alle offenen MAs freigeben + SMS senden')}
          >
            {bulkRunning ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Zap size={14} />
            )}
            {t('zeitkontoBestaetigung.admin.quickAllOffen', 'Alle offenen freigeben')} ({offenCount})
          </button>
        </div>
      </div>

      {/* Status-Cards (Earney-Style: schlank, Icon farbig, KPI-Wert primary) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(BEST_STATUS).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const active = activeFilter === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`min-w-0 text-left border rounded-xl p-5 bg-[var(--bg-card,#fff)] transition-colors hover:border-[var(--text-tertiary,#6B7280)] ${active ? 'border-[#338edc]' : 'border-[var(--border-default,#E5E7EB)]'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={cfg.iconColor} />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] truncate">{t(`zeitkontoBestaetigung.status.${key}`, cfg.label)}</p>
              </div>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{counts[key] || 0}</p>
            </button>
          );
        })}
      </div>

      {/* Filter-Pills (Bewerber-Style: schlank, transparent inaktiv, hellfarbig aktiv) */}
      <div className="flex gap-1.5 flex-wrap items-center">
        <button
          key="alle"
          type="button"
          onClick={() => setActiveFilter('alle')}
          className="px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
          style={{
            background: activeFilter === 'alle' ? '#EBF4FD' : 'transparent',
            color: activeFilter === 'alle' ? '#338edc' : 'var(--text-secondary,#4B5563)',
            borderColor: activeFilter === 'alle' ? '#338edc' : 'var(--border-card,#E5E7EB)',
          }}
        >
          {t('zeitkontoBestaetigung.admin.filterAlle', 'Alle')} ({rows.length})
        </button>
        {Object.entries(BEST_STATUS).map(([key, cfg]) => {
          const active = activeFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors"
              style={{
                background: active ? cfg.pillBg : 'transparent',
                color: active ? cfg.pillColor : 'var(--text-secondary,#4B5563)',
                borderColor: active ? cfg.pillColor : 'var(--border-card,#E5E7EB)',
              }}
            >
              {t(`zeitkontoBestaetigung.status.${key}`, cfg.label)} ({counts[key] || 0})
            </button>
          );
        })}
      </div>

      {/* Bulk-Action-Bar (dezent, Earney-Info-Banner-Style) */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 flex-wrap">
          <div className="text-sm font-medium text-[var(--text-primary)]">
            {t('zeitkontoBestaetigung.admin.bulkAusgewaehlt', '{{n}} MAs ausgewählt', { n: selectedIds.size })}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={bulkRunning}
              onClick={doBulkFreigabeSelected}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/40 text-white text-sm font-semibold"
            >
              {bulkRunning ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <UserCheck size={14} />
              )}
              {t('zeitkontoBestaetigung.admin.bulkFreigabe', 'HR-Freigabe + SMS senden')}
            </button>
            <button
              type="button"
              disabled={bulkRunning}
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
            >
              {t('zeitkontoBestaetigung.admin.bulkClear', 'Auswahl aufheben')}
            </button>
          </div>
        </div>
      )}

      {banner && (
        <div className={`p-3 rounded-xl border text-sm flex items-center gap-2 ${
          banner.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
        }`}>
          {banner.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />} {banner.text}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-12 bg-[var(--bg-card,#fff)] text-center">
          <FileText size={48} className="mx-auto text-[var(--text-tertiary,#6B7280)] mb-4" />
          <p className="text-base font-medium text-[var(--text-secondary)]">
            {t('zeitkontoBestaetigung.admin.noData', 'Keine Zeitkonto-Daten fuer diesen Monat.')}
          </p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-12 bg-[var(--bg-card,#fff)] text-center">
          <Search size={36} className="mx-auto text-[var(--text-tertiary,#6B7280)] mb-3" />
          <p className="text-sm font-medium text-[var(--text-secondary)]">
            {t('zeitkontoBestaetigung.admin.keineErgebnisse', 'Keine Treffer für Filter/Suche.')}
          </p>
        </div>
      ) : (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-visible bg-[var(--bg-card,#fff)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleSelectAll}
                    disabled={visibleFreigabeFaehig.length === 0}
                    className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]/20"
                    title={t('zeitkontoBestaetigung.admin.selectAll', 'Alle freigabefähigen selektieren')}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('lohnverrechnung.col.name', 'Mitarbeiter')}</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">Soll</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">Ist</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">Delta</th>
                <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">ZA-Saldo</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('zeitkontoBestaetigung.admin.status', 'Status')}</th>
                <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('zeitkontoBestaetigung.admin.verlauf', 'Verlauf')}</th>
                <th className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('zeitkontoBestaetigung.admin.aktion', 'Aktion')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
              {filteredRows.map(r => {
                const status = r.bestaetigungs_status || 'offen';
                const cfg = BEST_STATUS[status] || BEST_STATUS.offen;
                const canFreigabe = isFreigabeFaehig(r);
                const canResend = status === 'hr_freigegeben';
                const canReset = ['hr_freigegeben', 'dn_abgelehnt', 'frist_abgelaufen'].includes(status);
                const isSelected = selectedIds.has(r.mitarbeiter_id);
                return (
                  <tr key={r.id} className={`hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors ${isSelected ? 'bg-[#338edc]/5' : ''}`}>
                    <td className="px-4 py-3">
                      {canFreigabe ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectRow(r.mitarbeiter_id)}
                          disabled={bulkRunning}
                          className="w-4 h-4 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]/20"
                        />
                      ) : (
                        (['offen', 'frist_abgelaufen'].includes(status) && freigabeBlockGrund(r)) ? (
                          <span title={freigabeBlockGrund(r)} className="inline-flex items-center justify-center w-4 h-4 cursor-help">
                            <Info size={13} className="text-[var(--text-muted,#9CA3AF)]" />
                          </span>
                        ) : (
                          <span className="inline-block w-4 h-4" />
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)]">{r.vorname} {r.nachname}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)] font-mono">{r.soll_stunden != null ? Number(r.soll_stunden).toFixed(1) : '—'}</td>
                    <td className="px-4 py-3 text-right text-[var(--text-primary)] font-mono">{r.ist_stunden != null ? Number(r.ist_stunden).toFixed(1) : '—'}</td>
                    <td className={`px-4 py-3 text-right font-mono ${Number(r.delta_stunden) < 0 ? 'text-red-600' : Number(r.delta_stunden) > 0 ? 'text-emerald-600' : 'text-[var(--text-primary)]'}`}>
                      {r.delta_stunden != null ? (Number(r.delta_stunden) > 0 ? '+' : '') + Number(r.delta_stunden).toFixed(1) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${Number(r.za_saldo) < 0 ? 'text-red-600' : 'text-[var(--text-primary)]'}`}>
                      {r.za_saldo != null ? Number(r.za_saldo).toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cfg.cls}`}>
                        {t(`zeitkontoBestaetigung.status.${status}`, cfg.label)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-tertiary,#6B7280)]">
                      {r.hr_freigabe_am && <div>HR: {fmtDate(r.hr_freigabe_am)}</div>}
                      {r.dn_bestaetigung_gesendet_am && <div>SMS: {fmtDate(r.dn_bestaetigung_gesendet_am)}</div>}
                      {r.dn_bestaetigung_am && <div>DN: {fmtDate(r.dn_bestaetigung_am)}</div>}
                      {r.frist_abgelaufen_am && <div className="text-amber-600">{t('zeitkontoBestaetigung.status.frist_abgelaufen', 'Frist abgelaufen')}: {fmtDate(r.frist_abgelaufen_am)}</div>}
                    </td>
                    <td className="px-4 py-3 text-center relative">
                      <button
                        type="button"
                        disabled={busyId === r.mitarbeiter_id}
                        onClick={() => setOpenMenu(openMenu === r.mitarbeiter_id ? null : r.mitarbeiter_id)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
                      >
                        {busyId === r.mitarbeiter_id
                          ? <div className="w-3.5 h-3.5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                          : <MoreHorizontal size={16} />}
                      </button>
                      {openMenu === r.mitarbeiter_id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                          <div className="absolute right-2 top-10 z-20 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl shadow-sm py-1 min-w-[220px] text-left">
                            {canFreigabe && (
                              <MenuItem icon={<UserCheck size={14} />} onClick={() => doAction(r, 'hr-freigabe')}>
                                {t('zeitkontoBestaetigung.admin.freigabe', 'HR-Freigabe + SMS senden')}
                              </MenuItem>
                            )}
                            {canResend && (
                              <MenuItem icon={<Send size={14} />} onClick={() => doAction(r, 'resend-sms')}>
                                {t('zeitkontoBestaetigung.admin.resend', 'SMS erneut senden')}
                              </MenuItem>
                            )}
                            {canReset && (
                              <MenuItem icon={<RotateCcw size={14} />} onClick={() => { setOpenMenu(null); setResetFor(r); setResetGrund(''); }}>
                                {t('zeitkontoBestaetigung.admin.reset', 'Zurueck auf Offen')}
                              </MenuItem>
                            )}
                            <MenuItem icon={<History size={14} />} onClick={() => { setOpenMenu(null); setAuditFor(r); }}>
                              {t('zeitkontoBestaetigung.admin.auditLog', 'Audit-Log')}
                            </MenuItem>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {auditFor && <AuditLogSlideover ma={auditFor} jahr={jahr} monat={mm} onClose={() => setAuditFor(null)} t={t} />}

      {resetFor && (
        <ResetDialog
          ma={resetFor}
          grund={resetGrund}
          setGrund={setResetGrund}
          onCancel={() => { setResetFor(null); setResetGrund(''); }}
          onConfirm={doReset}
          t={t}
        />
      )}
    </div>
  );
}

function MenuItem({ icon, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] text-left"
    >
      <span className="text-[var(--text-tertiary)]">{icon}</span>
      {children}
    </button>
  );
}

function ResetDialog({ ma, grund, setGrund, onCancel, onConfirm, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-2xl max-w-md w-full p-5">
        <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">
          {t('zeitkontoBestaetigung.admin.resetTitle', 'Zuruecksetzen')}
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mb-3">
          {ma.vorname} {ma.nachname} — {t('zeitkontoBestaetigung.admin.resetHint', 'Status wird auf "Offen" zurueckgesetzt. Token wird ungueltig.')}
        </p>
        <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
          {t('zeitkontoBestaetigung.admin.grund', 'Grund')} *
        </label>
        <textarea
          value={grund}
          onChange={e => setGrund(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder={t('zeitkontoBestaetigung.admin.grundPlaceholder', 'Warum wird zurueckgesetzt?')}
          className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:ring-2 focus:ring-[#338edc]/20 focus:border-[#338edc] outline-none resize-none"
        />
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button onClick={onConfirm} disabled={grund.trim().length < 3}
            className="px-4 py-2 rounded-lg bg-[#338edc] hover:bg-[#2b7bc4] disabled:bg-[#338edc]/40 text-white text-sm font-semibold">
            {t('zeitkontoBestaetigung.admin.resetConfirm', 'Zuruecksetzen')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditLogSlideover({ ma, jahr, monat, onClose, t }) {
  const [entries, setEntries] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api(`/api/zeitkonto/${ma.mitarbeiter_id}/${jahr}-${parseInt(monat, 10)}/audit-log`)
      .then(r => setEntries(Array.isArray(r?.data) ? r.data : []))
      .catch(e => setErr(e?.error || e?.message || 'Fehler'));
  }, [ma.mitarbeiter_id, jahr, monat]);

  const iconFor = (aktion) => {
    if (aktion === 'hr_freigegeben') return <UserCheck size={14} className="text-blue-600" />;
    if (aktion === 'sms_gesendet' || aktion === 'sms_reminder') return <Send size={14} className="text-blue-600" />;
    if (aktion === 'dn_eingesehen') return <Eye size={14} className="text-[var(--text-tertiary)]" />;
    if (aktion === 'dn_bestaetigt') return <Check size={14} className="text-emerald-600" />;
    if (aktion === 'dn_abgelehnt') return <UserX size={14} className="text-red-600" />;
    if (aktion === 'frist_abgelaufen') return <Clock size={14} className="text-amber-600" />;
    if (aktion === 'hr_zurueckgesetzt') return <RotateCcw size={14} className="text-[var(--text-tertiary)]" />;
    return <Info size={14} className="text-[var(--text-tertiary)]" />;
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-[var(--bg-page,#F8F9FC)] border-l border-[var(--border-default,#E5E7EB)] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--bg-card,#fff)] border-b border-[var(--border-default,#E5E7EB)] p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[var(--text-primary)]">{t('zeitkontoBestaetigung.admin.auditLogTitle', 'Audit-Log')}</h2>
            <p className="text-sm text-[var(--text-tertiary,#6B7280)]">{ma.vorname} {ma.nachname} — {fmtMonat(`${jahr}-${monat}`)}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]">
            <X size={16} className="text-[var(--text-secondary)]" />
          </button>
        </div>
        <div className="p-4">
          {err && <p className="text-sm text-red-600">{err}</p>}
          {!err && entries === null && <p className="text-sm text-[var(--text-tertiary)]">{t('common.loading', 'Lade...')}</p>}
          {entries && entries.length === 0 && (
            <p className="text-sm text-[var(--text-tertiary)]">{t('zeitkontoBestaetigung.admin.auditEmpty', 'Keine Eintraege.')}</p>
          )}
          {entries && entries.length > 0 && (
            <ol className="space-y-3">
              {entries.map(e => (
                <li key={e.id} className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    {iconFor(e.aktion)}
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {t(`zeitkontoBestaetigung.aktion.${e.aktion}`, e.aktion)}
                    </span>
                    <span className="ml-auto text-xs text-[var(--text-tertiary)]">
                      {new Date(e.ausgefuehrt_at).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' })}
                    </span>
                  </div>
                  {e.user_name && (
                    <div className="text-xs text-[var(--text-tertiary)] mt-1">
                      {t('zeitkontoBestaetigung.admin.durch', 'durch')} {e.user_name}
                    </div>
                  )}
                  {e.grund && (
                    <div className="text-sm text-[var(--text-secondary)] mt-1 italic">"{e.grund}"</div>
                  )}
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <div className="text-xs text-[var(--text-muted,#9CA3AF)] mt-1 font-mono">
                      {Object.entries(e.metadata).filter(([k]) => !['ip','user_agent'].includes(k)).map(([k,v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' • ')}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}


export default function LohnverrechnungPage() {
  const { t } = useLanguage();
  const { pathname } = useLocation();

  const view = pathname.endsWith('/import') ? 'import'
    : pathname.endsWith('/einstellungen') ? 'einstellungen'
    : pathname.endsWith('/lv-report') ? 'lv-report'
    : pathname.endsWith('/monats-bestaetigung') ? 'monats-bestaetigung'
    : 'archiv';

  return (
    <div className="p-6 space-y-6">
      <SharedLohnverrechnungTabs />
      {view === 'import' && <ImportView t={t} />}
      {view === 'einstellungen' && <EinstellungenView t={t} />}
      {view === 'lv-report' && <LvReportView t={t} />}
      {view === 'monats-bestaetigung' && <MonatsBestaetigungView t={t} />}
      {view === 'archiv' && <ArchiveView t={t} />}
    </div>
  );
}
